import {ApplySuggestionDialog} from '@github-ui/diff-lines/apply-suggestion-dialog'
import type {SuggestionDetails, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {DiscardBatchConfirmationDialog} from '@github-ui/diff-lines/discard-batch-confirmation-dialog'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import {updateQueryParam} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {
  WhitespacePreference,
  type WhitespacePreference as WhitespacePreferenceValue,
} from '@github-ui/diff-view-settings/components/WhitespacePreferenceFlyout'
import {CompactDiffLinesNotice} from './CompactDiffLinesNotice'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {Button, CounterLabel, Stack, Tooltip} from '@primer/react'
import {XIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'
import {useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {getSuggestionDetails} from '../../helpers/get-suggestion-details'
import {clearActiveBatchFromStorage, saveActiveBatchToStorage} from '../../helpers/suggestion-batch-storage'
import {useActiveRoute} from '../../hooks/use-active-route'
import {useHeaderLiveUpdates} from '../../hooks/use-header-live-updates'
import {useMergeStatusButtonData} from '../../hooks/use-merge-status-button-data'
import {useApplyBatchedSuggestionsMutation} from '../../mutations/use-apply-batched-suggestions-mutation'
import {useUpdateShowChangeGroupsPreferenceMutation} from '../../mutations/use-update-show-change-groups-preference-mutation'
import {useUpdateWhitespacePreferenceMutation} from '../../mutations/use-update-whitespace-preference-mutation'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import type {FilesRoutePayload, PageLimits} from '../../page-data/payloads/files'
import {FeatureState, type MergeStatusButtonData, type StackData} from '../../page-data/payloads/header'
import {
  useAllMarkers,
  useComparisonInfo,
  useIsSingleFileMode,
  useIsSuggestionBatchPending,
  usePullRequestBasePath,
  useShowChangeGroupsPreference,
  useSuggestionBatch,
  useSuggestionBatchActions,
  useCurrentUser,
} from '../../stores/selectors'
import {ChangesSelector} from '../diff-filtering/ChangesSelector'
import {PullRequestHeaderSummary} from '../PullRequestHeaderSummary'
import {PullRequestStateLabel} from '../PullRequestStateLabel'
import {ChangeGroupsPreferences} from './ChangeGroupsPreferences'
import {OpenAlertsPanelButton} from './OpenAlertsPanelButton'
import {OpenCommentsPanelButton} from './OpenCommentsPanelButton'
import {OpenOverviewPanelButton} from './OpenOverviewPanelButton'
import styles from './PullRequestFilesToolbar.module.css'
import {RefreshButton} from './RefreshButton'
import {ReviewMenuButton} from './ReviewMenuButton'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {ToolbarMoreMenu} from './ToolbarMoreMenu'
import {ViewedFileProgress} from './ViewedFileProgress'
import {OpenCopilotChatPanelButton} from './OpenCopilotChatPanelButton'
import {useFeatureState, useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {StackState} from '../StackState'
import {useCommitMessageGenerationConfig} from '../../hooks/use-commit-message-generation-config'
import {
  createLegacyBatchCommitMessageConfig,
  hasEligibleLegacyBatchCommitMessageItems,
} from '../../helpers/create-batch-commit-message-config'

export type PullRequestFilesToolbarProps = {
  viewerCanUnstack?: boolean
  changeGroupsPreferenceAvailable: boolean
  commits: FilesRoutePayload['commits']
  currentUserLogin?: string
  isFileTreeExpanded?: boolean
  lastReviewOid?: string
  mergeStatusButtonData?: MergeStatusButtonData
  pageLimits: PageLimits
  pullRequest: FilesRoutePayload['pullRequest']
  repository: FilesRoutePayload['repository']
  shouldShowViewedFilesCount: boolean
  stack?: StackData | null
  stackBaseBranch?: string
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
}

export function LivePullRequestFilesToolbar({
  repository,
  pullRequest: initialPullRequest,
  ...pullRequestFilesToolbarProps
}: Omit<PullRequestFilesToolbarProps, 'viewerCanUnstack' | 'mergeStatusButtonData' | 'stack'>) {
  const {
    data: {pullRequest, aliveChannels, mergeStatusButtonData, stack, user},
  } = useHeaderPageData()
  useHeaderLiveUpdates(aliveChannels)

  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled

  // Preserve viewerAllowedNonCommentReviewTypes from the initial payload. This property
  // is being lifted into the header data behind a feature flag for deploy safety, so we
  // don't want the header response to overwrite the initial value until rollout is complete.
  // See https://github.com/github/github/pull/429353 for more details. This can be removed
  // when the updated_pull_request_header feature flag is removed.
  const updatedPullRequest = {
    ...initialPullRequest,
    ...pullRequest,
    viewerAllowedNonCommentReviewTypes: initialPullRequest.viewerAllowedNonCommentReviewTypes,
  } as FilesRoutePayload['pullRequest']

  return (
    <PullRequestFilesToolbar
      repository={repository}
      pullRequest={updatedPullRequest}
      mergeStatusButtonData={mergeStatusButtonData}
      viewerCanUnstack={user.canEditTitle}
      stack={stack}
      stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
      {...pullRequestFilesToolbarProps}
    />
  )
}

export function PullRequestFilesToolbar({
  viewerCanUnstack = false,
  changeGroupsPreferenceAvailable,
  commits,
  currentUserLogin,
  isFileTreeExpanded,
  lastReviewOid,
  mergeStatusButtonData,
  pageLimits,
  pullRequest,
  repository,
  shouldShowViewedFilesCount,
  stack,
  stackBaseBranch,
  totalFilesCount,
  treeToggleElement,
}: PullRequestFilesToolbarProps) {
  const {mutate: updateWhitespace} = useUpdateWhitespacePreferenceMutation()
  const {clearSuggestionBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()
  const {mutate: applyBatchedSuggestions} = useApplyBatchedSuggestionsMutation(
    pullRequest.pathName,
    pullRequest.comparison.headOid,
  )
  const {helpUrl} = useLayoutRouteData()
  const {mutate: updateChangeGroupPreference} = useUpdateShowChangeGroupsPreferenceMutation()
  const showChangeGroups = useShowChangeGroupsPreference()
  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()
  const [isSticky, setIsSticky] = useState(false)
  // Display the divider only if our new feature is DISABLED AND one of the following is true
  const showUpdatedHeader = useIsOverviewEnabled()
  const persistentWhitespacePreferencesEnabled = isFeatureEnabled('pull_requests_persistent_whitespace_preferences')
  const showDivider = !showUpdatedHeader && (shouldShowViewedFilesCount || !isFileTreeExpanded)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const isSuggestionBatchPending = useIsSuggestionBatchPending()
  const suggestionBatch = useSuggestionBatch()
  const markers = useAllMarkers()
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false)
  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()
  const currentUser = useCurrentUser()
  const [whitespacePreference] = useState<WhitespacePreferenceValue>(() => {
    const {whitespacePreference: accountPreference, whitespacePreferenceSource} = currentUser.viewSettings

    if (whitespacePreferenceSource === 'pull_request_override') {
      return WhitespacePreference.HideForCurrentDiff
    }

    if (accountPreference === WhitespacePreference.AlwaysHide) {
      return WhitespacePreference.AlwaysHide
    }

    if (accountPreference === WhitespacePreference.AlwaysShow) {
      return WhitespacePreference.AlwaysShow
    }

    return comparisonInfo.hideWhitespace ? WhitespacePreference.HideForCurrentDiff : WhitespacePreference.AlwaysShow
  })
  const hasEligibleLegacyCcrItems = useMemo(
    () => (isCommitDialogOpen ? hasEligibleLegacyBatchCommitMessageItems(suggestionBatch.activeBatch, markers) : false),
    [isCommitDialogOpen, suggestionBatch.activeBatch, markers],
  )
  const generationAuthorization = useCommitMessageGenerationConfig({
    enabled: isCommitDialogOpen && hasEligibleLegacyCcrItems,
    basePath: pullRequestBasePath,
  })
  const generationConfig = useMemo(
    () =>
      isCommitDialogOpen
        ? createLegacyBatchCommitMessageConfig(suggestionBatch.activeBatch, markers, {
            authorization: generationAuthorization,
            pullRequestPath: pullRequestBasePath,
            headOid: comparisonInfo.fullDiff.headOid,
          })
        : undefined,
    [
      isCommitDialogOpen,
      suggestionBatch.activeBatch,
      markers,
      generationAuthorization,
      pullRequestBasePath,
      comparisonInfo.fullDiff.headOid,
    ],
  )

  const [changesSelectorOpen, setChangesSelectorOpen] = useState(false)

  const suggestionDetails = getSuggestionDetails(markers, suggestionBatch.activeBatch)
  const suggestionsCount = suggestionDetails.length

  const batchCommentKind = useMemo((): 'ccr' | 'human' | 'mixed' => {
    const kinds = new Set(suggestionBatch.activeBatch.map(s => s.commentKind).filter(Boolean))
    // `commentKind` is only used for the `comments.commit_suggestion` analytics event in
    // ApplySuggestionDialog and is optional on BatchedSuggestion (added after CCR shipped).
    // Default to 'human' when the field is missing on every entry — that matches the
    // historical default before CCR existed and preserves prior analytics behavior for
    // older batches restored from storage.
    if (kinds.size === 0) return 'human'
    if (kinds.size === 1) return [...kinds][0] ?? 'human'
    return 'mixed'
  }, [suggestionBatch.activeBatch])

  const onUpdateWhitespacePreference = (preference: WhitespacePreferenceValue) => {
    updateWhitespace(
      {preference},
      {
        onSuccess: () => {
          updateQueryParam('w', '')
          ssrSafeWindow?.location.reload()
        },
      },
    )
  }

  const onUpdateWhitespace = (hideWhitespace: boolean) => {
    updateWhitespace({hideWhitespace}, {onSuccess: () => ssrSafeWindow?.location.reload()})
  }

  const onShowWhitespace = () => {
    updateQueryParam('w', '0')
    ssrSafeWindow?.location.reload()
  }

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  const handleRemoveSuggestion = useCallback(
    (suggestionId: SuggestionIdentifier) => {
      removeSuggestionFromBatch(suggestionId)
      saveActiveBatchToStorage(
        pullRequestBasePath,
        comparisonInfo.fullDiff.headOid,
        suggestionBatch.activeBatch.filter(
          id =>
            !(
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex
            ),
        ),
      )
    },
    [removeSuggestionFromBatch, suggestionBatch, pullRequestBasePath, comparisonInfo],
  )

  const handleCommitBatch = useCallback(() => {
    sendAnalyticsEvent('comments.commit_suggested_change_from_batch', 'COMMIT_SUGGESTED_CHANGE_FROM_BATCH_BUTTON', {
      commentKind: batchCommentKind,
      surface,
      batchSize: suggestionBatch.activeBatch.length,
    })
    setIsCommitDialogOpen(true)
  }, [batchCommentKind, sendAnalyticsEvent, suggestionBatch.activeBatch.length, surface])

  const handleCommitDialogClose = useCallback(() => {
    setIsCommitDialogOpen(false)
  }, [])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const handleDiscardBatch = useCallback(() => {
    clearSuggestionBatch()
    clearActiveBatchFromStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid)
    handleCommitDialogClose()
    setShowDiscardConfirmation(false)
  }, [clearSuggestionBatch, pullRequestBasePath, comparisonInfo, handleCommitDialogClose])

  const handleCommit = useCallback(
    (commitMessage: string, onError: (error: Error) => void, onCompleted: () => void) => {
      applyBatchedSuggestions(
        {message: commitMessage},
        {
          onSuccess: () => {
            handleCommitDialogClose()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error)
          },
        },
      )
    },
    [applyBatchedSuggestions, handleCommitDialogClose],
  )
  const onShowChangeGroupsChange = (newVal: boolean) => {
    updateChangeGroupPreference({showChangeGroups: newVal})
  }

  const suggestions = suggestionDetails.map(
    (detail): SuggestionDetails => ({
      commentDatabaseId: detail.commentDatabaseId,
      suggestionIndex: detail.suggestionIndex,
      path: detail.path,
      bodyHTML: detail.bodyHTML,
      diffEntries: detail.diffEntries,
    }),
  )

  const sidePanelOpen = useIsDockedPanelOpen()

  // Merge status button setup
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const shouldShowMergeStatusButton = !showUpdatedHeader && validatedMergeData && isSticky && !sidePanelOpen
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel('mergeStatus', {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(
          stickyStyles.stickyHeader,
          styles.toolbar,
          isSticky && styles['is-stuck'],
          showUpdatedHeader && styles.updatedHeaderToolbar,
        )}
        data-side-panel-open={sidePanelOpen}
        data-file-tree-expanded={isFileTreeExpanded}
      >
        <h2 className="sr-only">Pull request toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
          {/* (Mini header already shows this) */}
          {!showUpdatedHeader && (
            <div className={styles['show-when-stuck']}>
              <PullRequestStateLabel state={pullRequest.state} />
            </div>
          )}
          <div
            className={clsx(
              // Only hide if NOT using updated header
              !showUpdatedHeader && styles['hide-when-stuck-large'],
              !showUpdatedHeader &&
                (isFileTreeExpanded
                  ? styles['hide-commit-selector-on-small']
                  : styles['hide-commit-selector-on-medium']),
            )}
          >
            <ChangesSelector
              commits={commits}
              lastReviewOid={lastReviewOid}
              ownerLogin={repository.ownerLogin}
              pullRequestNumber={pullRequest.number}
              repositoryName={repository.name}
              buttonClassName={styles['hide-icon-on-small']}
            />
          </div>
          {!isSuggestionBatchPending && persistentWhitespacePreferencesEnabled && comparisonInfo.hideWhitespace && (
            <Button
              variant="invisible"
              size="small"
              leadingVisual={XIcon}
              className="fgColor-accent"
              aria-label="Clear Hide whitespace filter"
              onClick={onShowWhitespace}
            >
              Hide whitespace
            </Button>
          )}
          {/* Only show PR title/summary when NOT using updated header (mini header already shows this) */}
          {!showUpdatedHeader && (
            <Stack direction="vertical" gap="none" className={clsx(styles['show-when-stuck'], 'min-width-0')}>
              <div className={clsx('d-flex mb-n1', styles['pr-title-details'])}>
                <Tooltip type="label" direction="s" text={pullRequest.title}>
                  <a href="#top" className="d-flex overflow-hidden fgColor-default">
                    <bdi className={clsx('f5 text-bold overflow-hidden no-wrap', styles['pr-sticky-title'])}>
                      {pullRequest.title}
                    </bdi>
                  </a>
                </Tooltip>
                <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
              </div>

              <div
                className={clsx(
                  'f6 text-normal d-flex flex-items-center ml-n2',
                  isFileTreeExpanded
                    ? styles['hide-commit-selector-on-small']
                    : styles['hide-commit-selector-on-medium'],
                )}
              >
                {/* Unmount when the toolbar is no longer sticky so the overlay doesn't outlive its anchor. */}
                {isSticky && stack && (
                  <>
                    <StackState
                      pullRequestNumber={pullRequest.number}
                      stack={stack}
                      viewerCanUnstack={viewerCanUnstack}
                      anchorVariant="compact"
                    />
                    <div className={styles.divider} />
                  </>
                )}
                <ChangesSelector
                  commits={commits}
                  lastReviewOid={lastReviewOid}
                  ownerLogin={repository.ownerLogin}
                  pullRequestNumber={pullRequest.number}
                  repositoryName={repository.name}
                  variant="condensed"
                  open={changesSelectorOpen}
                  onOpenChange={setChangesSelectorOpen}
                />
                <div className={clsx('d-none d-lg-block', styles.divider)} />
                <div
                  className={clsx('d-none d-lg-flex flex-items-center min-width-0 px-2', styles['pr-title-details'])}
                >
                  <PullRequestHeaderSummary
                    author={pullRequest.author}
                    attributedUser={pullRequest.attributedUser}
                    baseBranch={pullRequest.baseBranch}
                    baseRepositoryOwnerLogin={repository.ownerLogin}
                    baseRepositoryName={repository.name}
                    commitsCount={pullRequest.commitsCount}
                    headBranch={pullRequest.headBranch}
                    headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                    headRepositoryName={pullRequest.headRepositoryName}
                    isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                    mergedBy={pullRequest.mergedBy}
                    mergedByName={pullRequest.mergedByName}
                    state={pullRequest.state}
                    pullRequestId={pullRequest.id}
                    pullRequestNumber={pullRequest.number}
                    repositoryId={repository.id}
                  />
                </div>
              </div>
            </Stack>
          )}
        </Stack>
        <Stack direction="horizontal" align="center">
          <RefreshButton aliveChannel={pullRequest.aliveChannel} />

          <div className={clsx('d-flex', styles['file-controls'])}>
            {shouldShowViewedFilesCount && (
              <ViewedFileProgress
                totalFilesCount={totalFilesCount}
                className={styles['hide-viewed-progress-on-small']}
              />
            )}

            {isSingleFileMode && totalFilesCount > 1 && (
              <div
                className={clsx(
                  styles['file-controls-navigation'],
                  showUpdatedHeader && styles['hide-file-controls-on-small'],
                )}
              >
                <ReviewNavigationButtonGroup />
              </div>
            )}
          </div>
          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}
          {isSuggestionBatchPending ? (
            <>
              <Button aria-label="Discard batch" variant="danger" size="small" onClick={handleDiscardClick}>
                Discard<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> batch</span>
              </Button>
              <Button aria-label="Commit suggestions" variant="primary" size="small" onClick={handleCommitBatch}>
                Commit<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> suggestions</span>{' '}
                <CounterLabel className="ml-1 color-fg-inherit">{suggestionsCount}</CounterLabel>
              </Button>
            </>
          ) : (
            <>
              {shouldShowMergeStatusButton && (
                <MergeStatusButtonWrapper
                  channels={validatedMergeData.channels}
                  headSha={validatedMergeData.headSha}
                  helpUrl={helpUrl}
                  viewerLogin={validatedMergeData.viewerLogin}
                  isDockedMode
                  onToggleDockedPanel={handleToggleDockedMergePanel}
                  mergeStatusButtonRef={mergeStatusButtonRef}
                  size="small"
                />
              )}
              {currentUserLogin && !showUpdatedHeader && (
                <ReviewMenuButton
                  currentUserLogin={currentUserLogin}
                  headRefOid={pullRequest.comparison.headOid}
                  pullRequest={pullRequest}
                  repository={repository}
                  stackBaseBranch={stackBaseBranch}
                />
              )}
              <div className={styles['container-show-on-sm']}>
                <DiffViewSettings
                  ariaLabel={showUpdatedHeader ? 'Diff settings' : undefined}
                  invisible={showUpdatedHeader}
                  small
                  {...(persistentWhitespacePreferencesEnabled
                    ? {
                        whitespacePreference,
                        onUpdateWhitespacePreference,
                      }
                    : {
                        defaultHideWhitespace: comparisonInfo.hideWhitespace,
                        onUpdateWhitespace,
                      })}
                  additionalPreferences={
                    <ChangeGroupsPreferences
                      changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                      showChangeGroups={showChangeGroups}
                      onShowChangeGroupsChange={onShowChangeGroupsChange}
                    />
                  }
                />
                <CompactDiffLinesNotice />
              </div>
            </>
          )}
          {!showUpdatedHeader && (
            <>
              <div className={styles['file-controls-divider']} />
              <div className="d-flex gap-1">
                <OpenOverviewPanelButton />
                <div className={styles['container-show-on-sm']}>
                  <OpenCommentsPanelButton pageLimits={pageLimits} />
                </div>
                <OpenAlertsPanelButton buttonClasses="d-none d-sm-flex" hideIfNoAlerts />
                <OpenCopilotChatPanelButton />
              </div>
            </>
          )}
          <div className={isFileTreeExpanded ? styles['show-more-menu-on-small'] : styles['show-more-menu-on-medium']}>
            <ToolbarMoreMenu
              hideWhitespace={comparisonInfo.hideWhitespace}
              onUpdateWhitespace={onUpdateWhitespace}
              whitespacePreference={whitespacePreference}
              onUpdateWhitespacePreference={onUpdateWhitespacePreference}
              showCommitSelector={() => setChangesSelectorOpen(true)}
              commits={commits}
              isFileTreeExpanded={isFileTreeExpanded}
              additionalPreferences={
                <ChangeGroupsPreferences
                  changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                  showChangeGroups={showChangeGroups}
                  onShowChangeGroupsChange={onShowChangeGroupsChange}
                />
              }
            />
          </div>
        </Stack>
      </Stack>
      {isCommitDialogOpen && suggestions.length > 0 && (
        <ApplySuggestionDialog
          authorLogins={[]}
          batchSize={suggestionBatch.activeBatch.length}
          commentKind={generationConfig ? 'ccr' : batchCommentKind}
          generationConfig={generationConfig}
          onClose={handleCommitDialogClose}
          onCommit={handleCommit}
          defaultCommitMessage="Apply suggestions from code review"
          suggestions={suggestions}
          onRemoveSuggestion={handleRemoveSuggestion}
          onDiscardBatch={handleDiscardBatch}
        />
      )}
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardBatch}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestionsCount}
      />
    </>
  )
}
